import { useEffect } from 'react';

/**
 * Universal keyboard shortcuts for list/table bulk operations.
 *
 * - Cmd+A / Ctrl+A  → select all filtered items (prevents native text selection)
 * - Delete / Backspace → open bulk delete confirm (only when at least 1 item is selected)
 * - Escape          → clear selection (when at least 1 item is selected)
 *
 * Shortcuts are ignored when the user is typing into an <input>, <textarea>,
 * [contenteditable], <select>, or when a dialog is already open (so we don't
 * interfere with the dialog's own keyboard handling).
 *
 * @param {Object} opts
 * @param {Array}    opts.items            - currently filtered list (what ⌘A will select)
 * @param {Set}      opts.selected         - current selection
 * @param {Function} opts.setSelected      - selection setter (new Set)
 * @param {Function} opts.onDelete         - called on Suppr if selected.size > 0
 * @param {boolean}  [opts.enabled=true]   - turn shortcuts on/off
 */
export function useBulkSelectionKeyboard({ items, selected, setSelected, onDelete, enabled = true }) {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      // Skip if focus is on an editable control
      const target = e.target;
      const tag = (target?.tagName || '').toLowerCase();
      if (
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        target?.isContentEditable
      ) {
        return;
      }
      // Skip if any Radix modal is open (body is scroll-locked)
      if (document.body.dataset.scrollLocked !== undefined || document.body.hasAttribute('data-scroll-locked')) {
        return;
      }

      const isCmdA = (e.metaKey || e.ctrlKey) && (e.key === 'a' || e.key === 'A');
      if (isCmdA) {
        if (!items || items.length === 0) return;
        e.preventDefault();
        setSelected(new Set(items.map((it) => it.id)));
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected.size > 0) {
        // Backspace is commonly used to go back in Chrome when not inside an input — safe here
        // because we already filtered editable targets above
        e.preventDefault();
        onDelete?.();
        return;
      }
      if (e.key === 'Escape' && selected.size > 0) {
        e.preventDefault();
        setSelected(new Set());
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [items, selected, setSelected, onDelete, enabled]);
}

export default useBulkSelectionKeyboard;
